import { useTranslation } from 'react-i18next';
import { getDay, getYear, isToday } from 'date-fns';
import { formatDate, getLocale, weekdays } from '~/common/utils/dateUtil';
import DeliveryDaysLegend from '~/common/components/calendar/DeliveryDaysLegend';
import type { IDeliveryCalendarDtoData, IDeliveryDateDtoData } from '~/dtos/ProdegaApiWebshop-api-typings-generated';
import { cn } from '~/common/utils/styleUtil';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { Button } from '~/common/components/ui/button.tsx';

export function getContrastTextColorForRgb(rgb: { r: number; g: number; b: number }): string {
  // calculate contrast of color (standard grayscale algorithmic formula)
  const contrast = (Math.round(rgb.r * 299) + Math.round(rgb.g * 587) + Math.round(rgb.b * 114)) / 1000;
  return contrast >= 150 ? 'black' : 'white';
}

export default function TgsCalendar({
  isReadonly,
  onPreviousMonth,
  onNextMonth,
  selectedDate,
  onDateSelected,
  deliveryCalendar,
  reset
}: Readonly<{
  isReadonly?: boolean;
  onPreviousMonth: () => void;
  onNextMonth: () => void;
  selectedDate?: Date;
  onDateSelected?: (date: any) => void;
  deliveryCalendar: IDeliveryCalendarDtoData;
  reset: () => void;
}>) {
  const { i18n, t } = useTranslation();

  const monthFrom = deliveryCalendar.fromDate ? new Date(deliveryCalendar.fromDate) : null;
  const monthTill = deliveryCalendar.toDate ? new Date(deliveryCalendar.toDate) : null;

  const isValidMonthFrom = monthFrom instanceof Date && !Number.isNaN(monthFrom.getTime());

  function fillCalendarRowWithPlaceholders(days: IDeliveryDateDtoData[]): (IDeliveryDateDtoData | null)[] {
    if (!days.length) return new Array(7).fill(null);

    const firstDate = new Date(days[0].date);
    const jsDay = getDay(firstDate);
    const index = (jsDay + 6) % 7;

    const placeholdersBefore = new Array(index).fill(null);
    const fullRow = [...placeholdersBefore, ...days];

    while (fullRow.length < 7) {
      fullRow.push(null);
    }

    return fullRow;
  }

  // Zellen mit stabilen Keys aufbereiten, damit im JSX kein Array-Index als Key nötig ist (Sonar S6479)
  function buildCalendarRowCells(days: IDeliveryDateDtoData[], rowIndex: number) {
    return fillCalendarRowWithPlaceholders(days).map((day, dayIndex) => ({
      day,
      dayIndex,
      key: day ? new Date(day.date).getTime().toString() : `empty-${rowIndex}-${dayIndex}`
    }));
  }

  if (!isValidMonthFrom) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[300px] text-center gap-4">
        <div>{t('Keine Daten vorhanden')}</div>
        <div>
          <Button onClick={() => reset()}>{t('Zum aktuellen Monat')}</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full desktop:w-auto p-4 desktop:p-0 desktop:mt-4 flex flex-col lg:gap-8 lg:flex-row lg:items-end lg:max-w-none mr-2">
      <div className="flex-grow max-w-[700px]">
        {/* Header */}
        <div className="flex justify-between items-center mb-4">
          <button
            type="button"
            onClick={onPreviousMonth}
            className="p-2 rounded hover:bg-gray-200"
            id="calendar-arrow-left-button"
            data-testid={'calendar-prev-month-button'}
          >
            <ArrowLeft />
          </button>
          <div className="text-center">
            <div className="text-xl font-bold" data-testid={'calendar-year'}>
              {getYearLabel(monthFrom, monthTill)}
            </div>
            <div className="text-lg" data-testid={'calendar-month'}>
              {getMonthLabel(monthFrom, monthTill, i18n.language)}
            </div>
          </div>
          <button
            type="button"
            onClick={onNextMonth}
            className="p-2 rounded hover:bg-gray-200"
            id="calendar-arrow-right-button"
            data-testid={'calendar-next-month-button'}
          >
            <ArrowRight />
          </button>
        </div>

        {/* Weekdays */}
        <div className="grid grid-cols-8 gap-1 items-center text-center font-semibold mb-1">
          <div className="bg-gray-300 min-w-[38px] flex w-full aspect-square items-center justify-center">
            {' '}
            {t('KW')}
          </div>
          {weekdays(i18n.language).map(day => (
            <div key={day} className="bg-gray-300 min-w-[38px] flex w-full aspect-square items-center justify-center">
              {day.substring(0, 2)}
            </div>
          ))}
        </div>

        {/* Calendar */}
        {deliveryCalendar.groups?.map((row, rowIndex) => (
          <div
            key={row.days[0] ? new Date(row.days[0].date).getTime() : `week-${row.weekNumber}`}
            className="grid grid-cols-8 gap-1 items-center text-center mb-1"
          >
            {!!row.weekNumber && (
              <div className="bg-gray-200 flex w-full aspect-square items-center justify-center">{row.weekNumber}</div>
            )}
            {buildCalendarRowCells(row.days, rowIndex).map(cell =>
              cell.day ? (
                <CalendarDayCell
                  key={cell.key}
                  day={cell.day}
                  dayIndex={cell.dayIndex}
                  isReadonly={isReadonly}
                  selectedDate={selectedDate}
                  firstDeliveryDate={deliveryCalendar.firstDeliveryDate}
                  onDateSelected={onDateSelected}
                />
              ) : (
                <div key={cell.key} className="flex w-full aspect-square items-center justify-center" />
              )
            )}
          </div>
        ))}
      </div>

      {/* Legend */}
      <div className={'desktop:ml-4 mt-4'}>
        <DeliveryDaysLegend isReadonly={!!isReadonly} assortments={deliveryCalendar.assortments} />
      </div>
    </div>
  );
}

function CalendarDayCell({
  day,
  dayIndex,
  isReadonly,
  selectedDate,
  firstDeliveryDate,
  onDateSelected
}: Readonly<{
  day: IDeliveryDateDtoData;
  dayIndex: number;
  isReadonly?: boolean;
  selectedDate?: Date;
  firstDeliveryDate: IDeliveryCalendarDtoData['firstDeliveryDate'];
  onDateSelected?: (date: any) => void;
}>) {
  const textColor = getContrastTextColorForRgb({
    r: day.rcolor,
    g: day.gcolor,
    b: day.bcolor
  });
  const dayDate = new Date(day.date);
  const isSelected = selectedDate?.getTime() === dayDate.getTime();
  const isFirstDeliveryDate = new Date(firstDeliveryDate).getTime() === dayDate.getTime();

  let cursorClass = 'cursor-not-allowed';
  if (isReadonly) {
    cursorClass = 'cursor-default';
  } else if (day.selectable) {
    cursorClass = 'cursor-pointer';
  }

  return (
    <div>
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events */}
      <div
        className={cn(
          'flex w-full aspect-square items-center justify-center',
          cursorClass,
          isSelected ? 'bg-primary text-white' : `bg-gray-100 text-${textColor}`,
          isToday(day.date) ? 'border-4 border-secondary' : '',
          !day.selectable && 'opacity-70',
          isFirstDeliveryDate && 'border-4 border-primary'
        )}
        {...(!isSelected && {
          style: {
            backgroundColor: `rgb(${day.rcolor}, ${day.gcolor}, ${day.bcolor})`
          }
        })}
        onClick={!isReadonly && day.selectable ? () => onDateSelected?.(day) : undefined}
        id={`calendar-day-${dayIndex}`}
        data-testid={`calendar-day-${dayIndex}`}
      >
        {formatDate(dayDate, 'd')}
      </div>
    </div>
  );
}

function getYearLabel(monthFrom: Date | null, monthTill: Date | null) {
  const yearFrom = monthFrom ? getYear(monthFrom) : null;
  const yearTill = monthTill && monthFrom && getYear(monthFrom) !== getYear(monthTill) ? getYear(monthTill) : null;
  const yearTillSuffix = yearTill ? ` - ${yearTill}` : '';
  return `${yearFrom || ''}${yearTillSuffix}`;
}

function getMonthLabel(monthFrom: Date | null, monthTill: Date | null, language: string) {
  const fromLabel = monthFrom ? formatDate(monthFrom, 'MMMM', getLocale(language)) : '...';
  const tillLabel = monthTill ? formatDate(monthTill, 'MMMM', getLocale(language)) : '';
  const tillSuffix = tillLabel ? ` - ${tillLabel}` : '';
  return `${fromLabel}${tillSuffix}`;
}
